import config from '@/config'
import mixpanelUtils from '@/utils/mixpanel.utils'
import eventCategories from '@/constant/eventCategories'
// ============================================
// SOUND EFFECT 2026 - Import paths
// ============================================
import handClapMp3 from '@/assets/audio/sfx-2026/hand-clap.mp3'
import drumMp3 from '@/assets/audio/sfx-2026/drumroll.mp3'
import confettiMp3 from '@/assets/audio/sfx-2026/confetti.mp3'
import spinnerTickMp3 from '@/assets/audio/spinner-wheel-tick.mp3' // to be removed
import spinnerCelebrationMp3 from '@/assets/audio/spinner-wheel-celebration.mp3' // to be removed
import clockTickMp3 from '@/assets/audio/sfx-2026/clock-tick.mp3'
import countdown5Mp3 from '@/assets/audio/sfx-2026/countdown-to-show-question.mp3'
import countdown3Mp3 from '@/assets/audio/sfx-2026/countdown-to-end-question.mp3'
import lobbyToQuestionMp3 from '@/assets/audio/sfx-2026/lobby-to-question.mp3'
import startQuizMp3 from '@/assets/audio/sfx-2026/start-quiz.mp3'
import endNowMp3 from '@/assets/audio/sfx-2026/end-now-button.mp3'
import showAnswerMp3 from '@/assets/audio/show-answer.mp3'
import leaderboardEmptyMp3 from '@/assets/audio/sfx-2026/leaderboard-no-data.mp3'
import leaderboardMiddleMp3 from '@/assets/audio/sfx-2026/middle-leaderboard.mp3'
import leaderboardFinalMp3 from '@/assets/audio/sfx-2026/final-leaderboard.mp3'
import slideNavigationMp3 from '@/assets/audio/sfx-2026/slide-navigation.mp3'
import spinnerTick1Mp3 from '@/assets/audio/sfx-2026/spinner-wheel/spinner-wheel-tick-1.mp3'
import spinnerTick2Mp3 from '@/assets/audio/sfx-2026/spinner-wheel/spinner-wheel-tick-2.mp3'
import spinnerTick3Mp3 from '@/assets/audio/sfx-2026/spinner-wheel/spinner-wheel-tick-3.mp3'
import spinnerTick4Mp3 from '@/assets/audio/sfx-2026/spinner-wheel/spinner-wheel-tick-4.mp3'
import spinnerWheelShowWheelMp3 from '@/assets/audio/sfx-2026/spinner-wheel/show-spinning-wheel.mp3'
import spinnerWheelResultMp3 from '@/assets/audio/sfx-2026/spinner-wheel/spinning-wheel-result.mp3'
import buttonClickMp3 from '@/assets/audio/sfx-2026/button_click.mp3'
import showHideResultIconClickMp3 from '@/assets/audio/sfx-2026/show-hide-result-icon-click.mp3'
import toggleQrCodeMp3 from '@/assets/audio/sfx-2026/toggle-qr-code.mp3'

import dataRevealMp3 from '@/assets/audio/sfx-2026/data-reveal.mp3'
import focusItemMp3 from '@/assets/audio/sfx-2026/focus-item.mp3'
import flipTheBoxMp3 from '@/assets/audio/sfx-2026/flip-the-box.mp3'
import groupSfx from '@/assets/audio/sfx-2026/group.mp3'
import ungroupSfx from '@/assets/audio/sfx-2026/ungroup.mp3'
import { SLIDE_TYPES_MAP } from '@/constant/slideTypes'
import QuizStatuses from '@/constant/QuizStatuses'
// Music sources

// ============================================
// SOUND EFFECT SOURCES - Only paths, lazy load
// ============================================
const SOUND_EFFECT_SOURCES = {
  HAND_CLAP: handClapMp3,
  DRUM: drumMp3,
  CONFETTI: confettiMp3,
  SPINNER_TICK: spinnerTickMp3,
  SHOW_QUESTION: spinnerTickMp3, // TODO: replace the actual show_question sound
  SPINNER_CELEBRATION: spinnerCelebrationMp3,
  LOBBY_TO_QUESTION: lobbyToQuestionMp3,
  COUNTDOWN_TO_SHOW_QUESTION: countdown5Mp3,
  COUNTDOWN_TO_END_QUESTION: countdown3Mp3,
  CLOCK_TICK: clockTickMp3,
  START_QUIZ: startQuizMp3,
  END_NOW: endNowMp3,
  REVEAL_ANSWER: showAnswerMp3,
  SLIDE_NAVIGATION: slideNavigationMp3,
  LEADERBOARD_EMPTY: leaderboardEmptyMp3,
  LEADERBOARD_MIDDLE: leaderboardMiddleMp3,
  LEADERBOARD_FINAL: leaderboardFinalMp3,
  SPINNER_TICK_1: spinnerTick1Mp3,
  SPINNER_TICK_2: spinnerTick2Mp3,
  SPINNER_TICK_3: spinnerTick3Mp3,
  SPINNER_TICK_4: spinnerTick4Mp3,
  SPINNER_WHEEL_SHOW: spinnerWheelShowWheelMp3,
  SPINNER_WHEEL_RESULT: spinnerWheelResultMp3,
  BUTTON_CLICK: buttonClickMp3,
  SHOW_HIDE_RESULT_CLICK: showHideResultIconClickMp3,
  TOGGLE_QR_CODE: toggleQrCodeMp3,
  DATA_REVEAL: dataRevealMp3,
  FOCUS_ITEM: focusItemMp3,
  FLIP_THE_BOX: flipTheBoxMp3,
  GROUP: groupSfx,
  UNGROUP: ungroupSfx,
}

const MUSIC_SOURCES = {
  DEFAULT_QUIZ_LOBBY_MUSIC: `${config.cloudfrontPublic}/BGM/UpliftFull.mp3`,
}

// ============================================
// CACHE - Store created Audio instances
// ============================================
const soundEffectCache = {}
const musicCache = {}

// ============================================
// FACTORY FUNCTIONS - Lazy load with cache
// ============================================

/**
 * Get sound effect by key - lazy load and cache
 * @param {string} key - Sound effect key (e.g., 'CONFETTI', 'HAND_CLAP')
 * @returns {Audio|null} - Audio instance or null if key not found
 */
export const getSoundEffect = (key) => {
  if (!soundEffectCache[key]) {
    const src = SOUND_EFFECT_SOURCES[key]
    if (!src) return null
    soundEffectCache[key] = new Audio(src)
  }
  return soundEffectCache[key]
}

/**
 * Get music by key - lazy load and cache
 * @param {string} key - Music key (e.g., 'LOBBY', 'PLAYING_QUIZ')
 * @returns {Audio|null} - Audio instance or null if key not found
 */
export const getMusic = (key) => {
  if (!musicCache[key]) {
    const src = MUSIC_SOURCES[key]
    if (!src) return null
    musicCache[key] = new Audio(src)
  }
  return musicCache[key]
}

/**
 * Create dynamic music from URL (for background music, slide audio)
 * @param {string} url - Music URL
 * @param {Object} options - Options (loop, preload)
 * @returns {Audio} - New Audio instance
 */
export const createDynamicMusic = (url, options = {}) => {
  const audio = new Audio(url)
  if (options.loop) audio.loop = true
  if (options.preload) audio.preload = options.preload
  return audio
}

// Export keys for type safety
export const SOUND_EFFECT_KEYS = Object.keys(SOUND_EFFECT_SOURCES)
export const MUSIC_KEYS = Object.keys(MUSIC_SOURCES)

// ============================================
// HELPER FUNCTIONS
// ============================================

const playSound = (sound) => {
  sound.play().catch(() => { })
}

const stopSound = (sound) => {
  sound.pause()
  // eslint-disable-next-line no-param-reassign
  sound.currentTime = 0
}

// ============================================
// STATE
// ============================================
const _state = {
  activeSounds: [], // Active sound effects
  musicHTMLAudioElement: null, // the <audio> element for background music
  currentMusicSrc: null, // Reactive source for music
}

// ============================================
// GETTERS
// ============================================
const _getters = {
  isEnableSoundEffects: (state, getters, rootState) => {
    if (!rootState.presentation?.isEnableQuizMusic) return false
    return rootState.presentation?.isEnableSoundEffects ?? true
  },
  isEnableSoundEffectsForAudience: (state, getters, rootState) => {
    return rootState.presentation?.isEnableSoundEffectsForAudience ?? false
  },
  isEnableMusic: (state, getters, rootState) => {
    if (!rootState.presentation?.isEnableQuizMusic) return false
    return rootState.presentation?.isEnableMusic ?? true
  },
  musicSrc: (state) => {
    return state.currentMusicSrc
  },
}

// ============================================
// MUTATIONS
// ============================================
const mutations = {
  ADD_ACTIVE_SOUND(state, sound) {
    if (!state.activeSounds.includes(sound)) {
      state.activeSounds.push(sound)
    }
  },
  REMOVE_ACTIVE_SOUND(state, sound) {
    const index = state.activeSounds.indexOf(sound)
    if (index > -1) {
      state.activeSounds.splice(index, 1)
    }
  },
  CLEAR_ALL_ACTIVE_SOUNDS(state) {
    // eslint-disable-next-line no-param-reassign
    state.activeSounds = []
  },
  SET_ACTIVE_MUSIC(state, music) {
    // eslint-disable-next-line no-param-reassign
    state.currentMusicSrc = music
    if (!state.musicHTMLAudioElement) return
    state.musicHTMLAudioElement.src = music
  },
  SET_AUDIO_ELEMENT(state, audioElement) {
    // eslint-disable-next-line no-param-reassign
    state.musicHTMLAudioElement = audioElement
  },
  SET_CURRENT_MUSIC_SRC(state, src) {
    // eslint-disable-next-line no-param-reassign
    state.currentMusicSrc = src
  },
}

// ============================================
// ACTIONS
// ============================================
const actions = {
  toggleMusicAndSfxAction({
    dispatch, getters,
  }) {
    const newValue = !(getters.isEnableSoundEffects || getters.isEnableMusic)

    dispatch('toggleMusicAction', newValue)
    dispatch('toggleSoundEffectAction', newValue)
  },

  // ---- Sound Effects ----
  // Toggle: on = resume, off = pause
  toggleSoundEffectAction({
    state, dispatch, rootState, getters,
  }, overrideValue) {
    const newValue = overrideValue !== undefined ? overrideValue : !getters.isEnableSoundEffects

    // Mute/unmute active sounds (keep playing to stay in sync with animation)
    state.activeSounds.forEach((sound) => {
      // eslint-disable-next-line no-param-reassign
      sound.muted = !newValue
    })

    // Persist to API (update presentation)
    const presentationId = rootState.presentation?.id
    if (presentationId) {
      dispatch('updatePresentationByIdAction', {
        id: presentationId,
        payload: {
          isEnableSoundEffects: newValue,
          isEnableQuizMusic: newValue, // set the legacy value as well
        },
      }, { root: true })
    }
    mixpanelUtils.track(
      'Sound Effects > Toggle',
      {
        event_category: eventCategories.PRESENTATION_EDITOR,
        event_label: newValue,
        presentation_id: rootState.presentation?.id,
      },
    )
  },

  toggleSoundEffectForAudienceAction({
    dispatch, rootState, getters,
  }, overrideValue) {
    const newValue = overrideValue !== undefined ? overrideValue : !getters.isEnableSoundEffectsForAudience

    // Persist to API (update presentation)
    const presentationId = rootState.presentation?.id
    if (presentationId) {
      dispatch('updatePresentationByIdAction', {
        id: presentationId,
        payload: {
          isEnableSoundEffectsForAudience: newValue,
        },
      }, { root: true })
    }
    mixpanelUtils.track(
      'Audience Sound Effects > Toggle',
      {
        event_category: eventCategories.PRESENTATION_EDITOR,
        event_label: newValue,
        presentation_id: presentationId,
      },
    )
  },

  /**
   * Play sound effect by key (lazy load)
   * @param {string} key - Sound effect key (e.g., 'CONFETTI')
   * @returns {Audio|null} - Audio instance or null
   */
  async playSoundEffectAction({
    state, getters, commit, rootGetters, rootState,
  }, key) {
    if (!rootGetters['featureToggles/canUseNewSfxSettings']) return
    // If users already turned off the legacy Quiz Sound Effect (on General Quiz settings > Sound effects)
    // don't play the new SFX for them
    if (!rootState?.presentation?.isEnableQuizMusic) return
    if (!rootGetters.isPresenting) return
    const sound = getSoundEffect(key)
    if (!sound) return null

    // Skip if already active (playing or muted-playing)
    if (state.activeSounds.includes(sound)) return sound

    commit('ADD_ACTIVE_SOUND', sound)
    // Auto-remove from activeSounds when playback ends
    sound.onended = () => {
      commit('REMOVE_ACTIVE_SOUND', sound)
    }
    stopSound(sound) // Reset to start
    sound.muted = !getters.isEnableSoundEffects
    playSound(sound) // Always play to keep time in sync
    return sound
  },

  /**
   * Stop sound effect by key
   * @param {string} key - Sound effect key
   */
  stopSoundEffectAction({ commit }, key) {
    const sound = getSoundEffect(key)
    if (!sound) return

    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  /**
   * Play sound effect instance directly (for dynamic sounds)
   * @param {Audio} sound - Audio instance
   */
  playSoundInstanceAction({ state, getters, commit }, sound) {
    if (!sound) return
    if (state.activeSounds.includes(sound)) return

    commit('ADD_ACTIVE_SOUND', sound)
    sound.onended = () => {
      commit('REMOVE_ACTIVE_SOUND', sound)
    }
    stopSound(sound)
    // eslint-disable-next-line no-param-reassign
    sound.muted = !getters.isEnableSoundEffects
    playSound(sound)
  },

  /**
   * Stop sound effect instance directly
   * @param {Audio} sound - Audio instance
   */
  stopSoundInstanceAction({ commit }, sound) {
    if (!sound) return
    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  /**
   * Play sound effect with interrupt+restart behavior (for rapid-fire sounds like spinner tick)
   * Unlike playSoundEffectAction, this does NOT track in activeSounds (zero Vuex mutations)
   * and always restarts the sound from the beginning with throttle to limit call frequency
   * @param {Object} payload - { key: string, throttleMs?: number }
   */
  playRapidSoundEffectAction({ getters, rootGetters }, { key, throttleMs = 45 }) {
    if (!rootGetters.isPresenting) return
    if (!getters.isEnableSoundEffects) return
    const sound = getSoundEffect(key)
    if (!sound) return
    const now = Date.now()
    if (now - (sound._lastPlay || 0) < throttleMs) return
    sound._lastPlay = now
    stopSound(sound)
    playSound(sound)
  },

  registerSoundAction({ commit }, sound) {
    commit('ADD_ACTIVE_SOUND', sound)
  },

  unregisterSoundAction({ commit }, sound) {
    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  // ---- Music ----
  // Toggle: on = unmute, off = mute (keep playing to maintain time position)
  toggleMusicAction({
    state, dispatch, rootState, getters,
  }, overrideValue) {
    const newValue = overrideValue !== undefined ? overrideValue : !getters.isEnableMusic
    // Mute/unmute active musics (keep playing to stay in sync)
    if (state.musicHTMLAudioElement) {
      if (newValue) {
        state.musicHTMLAudioElement.play()
      } else {
        state.musicHTMLAudioElement.pause()
      }
    }

    // Persist to API (update presentation)
    const presentationId = rootState.presentation?.id
    if (presentationId) {
      dispatch('updatePresentationByIdAction', {
        id: presentationId,
        payload: {
          isEnableMusic: newValue,
          isEnableQuizMusic: newValue,
        },
      }, { root: true })
    }

    mixpanelUtils.track(
      'Music > Toggle',
      {
        event_category: eventCategories.PRESENTATION_EDITOR,
        event_label: newValue,
        presentation_id: presentationId,
      },
    )
  },

  initAudioElementForMusicAction({ commit, rootState }, audioElement) {
    if (!audioElement) return
    const initMusicSrc = rootState.slideActive.type === 'pickAnswer'
      ? rootState.presentation.audioLink?.url || MUSIC_SOURCES.DEFAULT_QUIZ_LOBBY_MUSIC
      : rootState.slideActive.audioLink?.url || rootState.presentation.audioLink?.url
    commit('SET_CURRENT_MUSIC_SRC', initMusicSrc)
    commit('SET_AUDIO_ELEMENT', audioElement)
  },

  // ---- Stop All (when exiting presenting mode) ----
  /**
   * Stop all active sounds and musics
   * Called when user exits presenting mode
   */
  stopAllAction({ state, commit, dispatch }) {
    // Stop all sound effects
    state.activeSounds.forEach(stopSound)
    commit('CLEAR_ALL_ACTIVE_SOUNDS')

    dispatch('stopMusicAction')
  },

  stopAllSoundEffectsAction({ state, commit }) {
    state.activeSounds.forEach(stopSound)
    commit('CLEAR_ALL_ACTIVE_SOUNDS')
  },

  setActiveMusicAction({ commit }, music) {
    commit('SET_ACTIVE_MUSIC', music)
  },
  stopMusicAction({ state, commit }) {
    if (!state.musicHTMLAudioElement) return
    state.musicHTMLAudioElement.src = ''
    state.musicHTMLAudioElement.removeAttribute('src')
    commit('SET_CURRENT_MUSIC_SRC', null)
    state.musicHTMLAudioElement.pause()
  },
  playMusicAction({ state, rootState, commit }, musicSrc) {
    if (!musicSrc) return
    if (rootState.sharePresentation) return
    if (rootState?.presentation?.isEnableQuizMusic === false) return

    if (state.currentMusicSrc !== musicSrc) {
      if (state.musicHTMLAudioElement) {
        state.musicHTMLAudioElement.src = musicSrc
      }
      commit('SET_CURRENT_MUSIC_SRC', musicSrc)
    }
    if (rootState.presentation?.isEnableMusic) {
      state.musicHTMLAudioElement?.play()
    }
  },
  preloadMusicAndSoundEffectAction({ rootState }) {
    const slideMusicUrl = rootState.slideActive?.audioLink?.url
    const presentationMusicUrl = rootState.presentation?.audioLink?.url

    const urlsToPreload = [slideMusicUrl, presentationMusicUrl].filter(Boolean)
    const uniqueUrls = [...new Set(urlsToPreload)]

    uniqueUrls.forEach((url) => {
      createDynamicMusic(url, { preload: 'auto' })
    })

    // Preload all sound effects to avoid delay on first play
    SOUND_EFFECT_KEYS.forEach((key) => {
      const sound = getSoundEffect(key)
      if (sound && sound.preload !== 'auto') {
        sound.preload = 'auto'
      }
    })
  },
}

const ACTION_HANDLERS = {
  'PRESENTATION/IS_LOADED': (store) => {
    store.dispatch('audio/preloadMusicAndSoundEffectAction')
  },
  'PRESENTATION/STOP_PRESENTING': (store) => {
    store.dispatch('audio/stopAllAction')
  },
  'PRESENTATION/TOGGLE_QR_CODE': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'TOGGLE_QR_CODE')
  },
  'QUIZ/LOBBY_SCREEN': (store) => {
    const lobbyMusic = store.state?.presentation?.audioLink?.url || MUSIC_SOURCES.DEFAULT_QUIZ_LOBBY_MUSIC
    store.dispatch('audio/playMusicAction', lobbyMusic)
  },
  'QUIZ/START_QUIZ': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'START_QUIZ')
  },
  'QUIZ/SHOW_QUESTION_NUMBER': (store) => {
    store.dispatch('audio/stopMusicAction')
  },
  'QUIZ/COUNTDOWN_TO_SHOW_QUESTION': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'LOBBY_TO_QUESTION')
    setTimeout(() => {
      store.dispatch('audio/playSoundEffectAction', 'COUNTDOWN_TO_SHOW_QUESTION')
    }, 200)
  },
  'QUIZ/SHOW_QUESTION': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'LOBBY_TO_QUESTION')
    const quizBackgroundMusic = store.state?.slideActive?.audioLink?.url || store.state?.presentation?.audioLink?.url
    store.dispatch('audio/playMusicAction', quizBackgroundMusic)
  },
  'QUIZ/COUNTDOWN_TO_END_QUESTION': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'COUNTDOWN_TO_END_QUESTION')
  },
  'QUIZ/CLOCK_TICK': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'CLOCK_TICK')
  },
  'QUIZ/REVEAL_ANSWER': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'REVEAL_ANSWER')
  },
  'SLIDE/NAVIGATION': (store, action) => {
    store.dispatch('audio/stopAllSoundEffectsAction')
    store.dispatch('audio/playSoundEffectAction', 'SLIDE_NAVIGATION')
    const { fromSlide, toSlide } = action.payload
    const nextSlideType = toSlide?.type
    const presentationMusicUrl = store.state?.presentation?.audioLink?.url
    const nextSlideMusicUrl = toSlide?.audioLink?.url
    const previousSlideHasMusic = fromSlide?.audioLink?.url ||
      (fromSlide.type === SLIDE_TYPES_MAP.PICK_ANSWER && fromSlide.quizStatus == QuizStatuses.LOBBY_SCREEN)
    if (
      previousSlideHasMusic && !nextSlideMusicUrl ||
      (nextSlideType === SLIDE_TYPES_MAP.PICK_ANSWER && toSlide.quizStatus != QuizStatuses.LOBBY_SCREEN)
    ) {
      store.dispatch('audio/stopMusicAction')
    }

    if (nextSlideType !== SLIDE_TYPES_MAP.PICK_ANSWER) {
      store.dispatch('audio/playMusicAction', nextSlideMusicUrl || presentationMusicUrl)
    }
  },
  'QUIZ/LEADERBOARD': (store, action) => {
    let leaderboardSfxKey = 'LEADERBOARD_MIDDLE'
    if (action.payload.isEmpty) {
      leaderboardSfxKey = 'LEADERBOARD_EMPTY'
    } else if (action.payload.isFinal) {
      leaderboardSfxKey = 'LEADERBOARD_FINAL'
    }
    store.dispatch('audio/playSoundEffectAction', leaderboardSfxKey)
  },
  BUTTON_CLICK: (store, action) => {
    const sfxKey = action.payload || 'BUTTON_CLICK'
    store.dispatch('audio/playSoundEffectAction', sfxKey)
  },
  'SPINNER_WHEEL/TICK': (store, action) => {
    const segNumber = action.payload?.segNumber ?? 1
    const idx = (segNumber - 1) % 4
    const key = `SPINNER_TICK_${idx + 1}`
    store.dispatch('audio/playRapidSoundEffectAction', { key, throttleMs: 45 })
  },
  'SPINNER_WHEEL/SHOW_SPINNING_WHEEL': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'SPINNER_WHEEL_SHOW')
  },
  'SPINNER_WHEEL/SPINNING_WHEEL_RESULT': (store) => {
    store.dispatch('audio/playSoundEffectAction', 'SPINNER_WHEEL_RESULT')
  },
  'SPINNER_WHEEL/DESTROY': (store) => {
    store.dispatch('audio/stopSoundEffectAction', 'SPINNER_WHEEL_RESULT')
  },

}

const audioPlugin = (store) => {
  store.subscribeAction((action) => {
    if (!store.getters['featureToggles/canUseNewSfxSettings']) return

    const handler = ACTION_HANDLERS[action.type]
    if (handler) {
      handler(store, action)
    }
  })
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
  plugins: [audioPlugin],
}
